import React, { Fragment } from 'react';

import { Input, TableColumnsType } from 'antd';

import List from 'components/_Common/List';
import dayjs from 'dayjs';
import { DATETIME_CLIENT_FORMAT } from 'internal-constants';
import { makeCpf } from 'lib/helpers/Domain.helper';
import { LoadedGuestLog, useGuestsLogs } from 'lib/providers/GuestsLogsContextProvider';
import { Guest } from 'types/Guest.type';

import { ExitGuestLogCell } from './ExitGuestLogCell';
import { GuestNameCell } from './GuestNameCell';
import { GuestsLogsActionsCell } from './GuestsLogsActionsCell';
import { GuestVehicleCell } from './GuestVehicleCell';

import './GuestsLogsList.css';

const COLUMNS: TableColumnsType<LoadedGuestLog> = [
    {
        title: 'Nome',
        dataIndex: ['guest', 'name'],
        render: (_, record) => <GuestNameCell guest={record.guest} />,
        key: 'guestName',
    },

    {
        title: 'Unidade',
        dataIndex: ['apartment', 'id'],
        render: (_, record) => [record.apartment.tower.name, record.apartment.name].join(' - ')
    },

    {
        title: 'CPF',
        dataIndex: ['guest', 'cpf'],
        render: value => makeCpf(value),
        key: 'guestCpf',
    },

    {
        title: 'Motivo',
        dataIndex: 'reason',
        key: 'reason'
    },

    {
        title: 'Entrada',
        dataIndex: 'entered_at',
        render: value => dayjs(value).format(DATETIME_CLIENT_FORMAT)
    },

    {
        title: 'Saída',
        dataIndex: 'exited_at',
        render: (value, record) => value !== null
            ? dayjs(value).format(DATETIME_CLIENT_FORMAT)
            : <ExitGuestLogCell id={record.id} enteredAt={record.entered_at} />
    },

    {
        title: 'Veículo',
        dataIndex: 'vehicle',
        render: (value: Guest.Vehicle.Model | null) => value !== null
            ? <GuestVehicleCell vehicle={value} />
            : '-'
    },

    {
        render: (_, record) => <GuestsLogsActionsCell guestLog={record} />,
    },
];

export function GuestsLogsList() {
    const [filteredLogs, setFilteredLogs] = React.useState<LoadedGuestLog[]>([]);
    const { guestsLogs, isLoading } = useGuestsLogs();

    const logs = React.useMemo(() => {
        if (filteredLogs.length > 0) {
            return filteredLogs;
        }

        return guestsLogs;
    }, [filteredLogs, guestsLogs]);

    return (
        <Fragment>
            <Input.Search
                placeholder="Pesquisar por nome, unidade, CPF ou placa"
                allowClear
                onClear={() => {
                    setFilteredLogs([]);
                }}
                onSearch={(value) => {
                    const searchValue = value.trim();

                    if (searchValue.length > 0) {
                        const filteredLogs = guestsLogs.filter(log => {
                            const guestName = log.guest.name.toLowerCase();
                            const guestCpf = log.guest.cpf.toLowerCase();
                            const vehiclePlate = log.vehicle?.plate.toLowerCase() || '';
                            const apartmentName = log.apartment.tower.name.toLowerCase() + ' ' + log.apartment.name.toLowerCase();

                            return guestName.includes(searchValue.toLowerCase()) || guestCpf?.includes(searchValue.toLowerCase()) || vehiclePlate.includes(searchValue.toLowerCase()) || apartmentName.includes(searchValue.toLowerCase());
                        });

                        setFilteredLogs(filteredLogs);
                    }

                    if (searchValue.length === 0) {
                        setFilteredLogs([]);
                    }
                }}
            />
            <List<LoadedGuestLog>
                columns={COLUMNS}
                data={logs}
                loading={isLoading}
                rowClassName={(record) => record.canceled_at !== null ? 'canceled_row' : ''}
            />
        </Fragment>
    );
}
